import { z } from 'zod';
import type { UserToken } from '@sleed/bizcommerce-api';

/**
 * User edit form. Mirrors the `UserToken` DTO and the legacy admin grid validation
 * (Core/client/admin/src/pages/System/Users/index.js): rid required, eml required+email ≤255,
 * fn/ln ≤50, si required. Password (`psw`) is required when creating but optional on edit
 * (blank = keep current) — exactly the legacy `!isEditing ? RequiredRule` behavior.
 *
 * `letp`/`leid` (linked-entity type/id) are not shown but carried through so an update never
 * wipes them — matching the legacy onDataGridRowUpdatingMergeUnchanged.
 */
export const userSchema = z
	.object({
		id: z.number().optional(),
		rid: z.number().int().positive('validation.required'),
		eml: z.string().trim().min(1, 'validation.required').max(255, 'validation.tooLong').email('validation.email'),
		fn: z.string().trim().max(50, 'validation.tooLong').nullable(),
		ln: z.string().trim().max(50, 'validation.tooLong').nullable(),
		iv: z.boolean(),
		si: z.number({ message: 'validation.required' }).int(),
		psw: z.string(),
		letp: z.number().optional(),
		leid: z.number().nullable()
	})
	.superRefine((v, ctx) => {
		// Password required only when creating a new user.
		if (!v.id && v.psw.trim() === '') {
			ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['psw'], message: 'validation.required' });
		}
	});

export type UserFormValues = z.infer<typeof userSchema>;

export const EMPTY_USER: UserFormValues = { rid: 0, eml: '', fn: '', ln: '', iv: true, si: 0, psw: '', leid: null };

export function tokenToForm(token: UserToken): UserFormValues {
	return {
		id: token.id,
		rid: token.rid ?? 0,
		eml: token.eml ?? '',
		fn: token.fn ?? '',
		ln: token.ln ?? '',
		iv: token.iv ?? false,
		si: token.si ?? 0,
		psw: '', // never round-trip the password back into the form
		letp: token.letp,
		leid: token.leid ?? null
	};
}

export function formToToken(form: UserFormValues): UserToken {
	const psw = form.psw.trim();
	return {
		id: form.id,
		rid: form.rid,
		eml: form.eml.trim(),
		fn: form.fn?.trim() || null,
		ln: form.ln?.trim() || null,
		iv: form.iv,
		si: form.si,
		// Omit on edit when left blank so the password stays unchanged; always present on create.
		psw: psw === '' ? undefined : psw,
		letp: form.letp,
		leid: form.leid
	};
}
